import { ref } from "vue";
import type { Ref } from "vue";
import {
  type ShopListInput,
  type DeliveryGroupListInput,
  type DeliveryGroupGetInput,
  type DeliveryGroup,
  GetDeliveryGroupQuery,
  GetDeliveryGroupQueryVariables,
  GetDeliveryGroupDocument,
  type DeliveryCapOrdersListInput,
  type DeliveryGroupCapOrdersListInput,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import { getDeliveryGroupList } from "@/common/graphql/deliveryGroup";
import type {
  AreaListItem,
  ChainItem,
  ChainSelectValue,
  ShopItem,
  ShopSelectValue,
  DeliveryGroupIdItem,
  DeliveryGroupSelectValue,
  ListData,
  ListGroupData,
  TableHeaderItem,
} from "../types";

/**
 * チェーン・店舗・エリア・配達グループ・グループ便の選択連動制御、タブ切替
 */
export const useDeliveryCapSettingFilter = (params: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  processFlag: Ref<number>;
  mode: Ref<string>;
  selectShopFlg: Ref<boolean>;
  selectAreaFlg: Ref<boolean>;
  selectGroupFlg: Ref<boolean>;
  selectGroupIdFlg: Ref<boolean>;
  setIdFlg: Ref<boolean>;
  chainListItems: Ref<ChainItem[]>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopListItems: Ref<ShopItem[]>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaListItems: Ref<(AreaListItem | null)[]>;
  areaSelectValues: Ref<AreaListItem | null>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  deliveryGroupIdSelectValues: Ref<DeliveryGroupIdItem[]>;
  shopListInput: ShopListInput;
  deliveryGroupListInput: DeliveryGroupListInput;
  deliveryGroupGetInput: DeliveryGroupGetInput;
  getCapOrders: () => Promise<void>;
  getGroupCapOrders: () => Promise<void>;
  tableHeaders: Ref<TableHeaderItem[]>;
  listDataArray: Ref<ListData[]>;
  listGroupDataArray: Ref<ListGroupData[]>;
  mappedListData: Map<string, ListData[]>;
  mappedListGroupData: Map<string, ListGroupData[]>;
  areaParentData: Map<string, number>;
  displayDateFrom: Ref<string | null>;
  displayDateTo: Ref<string | null>;
  deliveryCapOrdersListInput: DeliveryCapOrdersListInput;
  deliveryGroupCapOrdersListInput: DeliveryGroupCapOrdersListInput;
}) => {
  const {
    errdialog,
    errMessage,
    processFlag,
    mode,
    selectShopFlg,
    selectAreaFlg,
    selectGroupFlg,
    selectGroupIdFlg,
    setIdFlg,
    chainListItems,
    chainSelectValues,
    shopListItems,
    shopSelectValues,
    areaListItems,
    areaSelectValues,
    deliveryGroupSelectValues,
    deliveryGroupIdSelectValues,
    shopListInput,
    deliveryGroupListInput,
    deliveryGroupGetInput,
    getCapOrders,
    getGroupCapOrders,
    tableHeaders,
    listDataArray,
    listGroupDataArray,
    mappedListData,
    mappedListGroupData,
    areaParentData,
    displayDateFrom,
    displayDateTo,
    deliveryCapOrdersListInput,
    deliveryGroupCapOrdersListInput,
  } = params;

  // 配達グループリスト（Filter内部のみ使用）
  const deliveryGroupListItems = ref<(DeliveryGroup | null | undefined)[]>([]);

  // 配達グループ便リスト（Filter内部のみ使用）
  const deliveryGroupItems = ref<DeliveryGroupIdItem[]>([]);

  /**
   * チェーン選択ボックス @change
   */
  const onChangeChain = async (): Promise<void> => {
    try {
      selectShopFlg.value = false;
      selectAreaFlg.value = false;
      selectGroupFlg.value = false;
      processFlag.value = 0;

      if (!shopSelectValues.value) {
        shopSelectValues.value = { shop_code: "", name: "" };
      } else {
        shopSelectValues.value.shop_code = "";
        shopSelectValues.value.name = "";
      }
      areaSelectValues.value = null;

      deliveryCapOrdersListInput.shop_code = "";
      deliveryCapOrdersListInput.area_code = "";

      shopListItems.value = [];
      areaListItems.value = [];

      if (!deliveryGroupSelectValues.value) {
        deliveryGroupSelectValues.value = { group_code: "", name: "" };
      } else {
        deliveryGroupSelectValues.value.group_code = "";
        deliveryGroupSelectValues.value.name = "";
      }

      deliveryGroupListItems.value = [];
      deliveryGroupItems.value = [];

      listDataArray.value = [];
      listGroupDataArray.value = [];

      const chainCode = chainSelectValues.value?.chain_code ?? "";
      if (!chainCode) {
        return;
      }

      // チェーンコードを設定
      shopListInput.chain_code = chainCode;
      deliveryCapOrdersListInput.chain_code = chainCode;
      deliveryGroupListInput.chain_code = chainCode;
      deliveryGroupGetInput.chain_code = chainCode;
      deliveryGroupCapOrdersListInput.chain_code = chainCode;

      if (mode.value === "shop") {
        // 店舗情報を取得する
        shopListInput.chain_code = chainCode;
        shopListItems.value = await getShopList(shopListInput);
      } else {
        // 配達グループ情報を取得する
        deliveryGroupListInput.chain_code = chainCode;
        const response = await getDeliveryGroupList(deliveryGroupListInput);
        deliveryGroupListItems.value = response?.map((item) => {
          if (item) {
            return {
              __typename: "DeliveryGroup", // 期待する __typename に変換
              chain_code: item.chain_code,
              customer_code: item.customer_code,
              group_code: item.group_code,
              name: item.name,
              result: {
                __typename: "Result",
                result: "",
                message: null,
              },
            } as DeliveryGroup;
          }
          return item;
        }) ?? [];
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
    }
  };

  /**
   * 店舗選択ボックス @change
   */
  const onChangeShop = async (): Promise<void> => {
    if (areaSelectValues.value) {
      areaSelectValues.value = null;
    }
    deliveryCapOrdersListInput.shop_code = "";
    deliveryCapOrdersListInput.area_code = "";

    areaListItems.value = [];
    listDataArray.value = [];
    processFlag.value = 0;

    selectShopFlg.value = false;
    selectAreaFlg.value = false;

    if (!shopSelectValues.value || shopSelectValues.value.shop_code === "") {
      return;
    }

    if (displayDateFrom.value && displayDateTo.value) {
      await getCapOrders();
      selectShopFlg.value = true;
      selectAreaFlg.value = false;
    }
  };

  /**
   * 絞り込み検索 @change
   */
  const onChangeArea = async (): Promise<void> => {
    processFlag.value = 0;

    selectAreaFlg.value = false;
    selectShopFlg.value = false;

    if (!areaSelectValues.value || areaSelectValues.value.area_code === "") {
      areaListItems.value = [];
      listDataArray.value = [];
      if (displayDateFrom.value && displayDateTo.value) {
        await getCapOrders();
      }
      return;
    }

    if (!displayDateFrom.value || !displayDateTo.value) {
      return;
    }

    await getCapOrders();

    selectShopFlg.value = false;
    selectAreaFlg.value = true;
  };

  /**
   * 配達グループ選択ボックス @change
   */
  const onChangeDeliveryGroup = async (): Promise<void> => {
    try {
      processFlag.value = 0;

      deliveryGroupIdSelectValues.value = [];
      deliveryGroupItems.value = [];
      listGroupDataArray.value = [];

      // 配達グループ名が空の場合、グループ便も空にする
      if (!deliveryGroupSelectValues.value || deliveryGroupSelectValues.value.group_code === "") {
        return;
      }

      deliveryGroupGetInput.group_code = deliveryGroupSelectValues.value.group_code;

      // 配達グループ便の取得
      const apolloClient = await generateApolloClient();
      const deliveryGroup = await apolloClient.query<
        GetDeliveryGroupQuery,
        GetDeliveryGroupQueryVariables
      >({
        query: GetDeliveryGroupDocument,
        variables: { input: deliveryGroupGetInput },
        fetchPolicy: "no-cache",
      });

      if (
        deliveryGroup.data.getDeliveryGroup &&
        deliveryGroup.data.getDeliveryGroup.response &&
        deliveryGroup.data.getDeliveryGroup.response.length > 0
      ) {
        deliveryGroup.data.getDeliveryGroup.response.forEach((value) => {
          if (value) {
            deliveryGroupItems.value.push({
              group_delivery_id: String(value.group_delivery_id),
              group_delivery_id_name:
                value.group_delivery_id === 0
                  ? "都度便"
                  : `${value.group_delivery_id}便`,
            });
          }
        });
      }

      if (displayDateFrom.value && displayDateTo.value) {
        selectGroupFlg.value = true;
        selectGroupIdFlg.value = false;
        setIdFlg.value = true;
        deliveryGroupIdSelectValues.value = [];
        await getGroupCapOrders();
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
    }
  };

  /**
   * 配達グループ便選択ボックス @change
   */
  const onChangeDeliveryGroupId = async (): Promise<void> => {
    try {
      processFlag.value = 0;

      listGroupDataArray.value = [];

      if (displayDateFrom.value && displayDateTo.value) {
        selectGroupFlg.value = false;
        selectGroupIdFlg.value = true;
        await getGroupCapOrders();
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
    }
  };

  /**
   * 店舗キャップ設定タブに変更する @Click
   */
  const onClickShopTab = async (): Promise<void> => {
    mode.value = "shop";

    // 編集フラグをOFFにする
    processFlag.value = 0;

    deliveryGroupSelectValues.value.group_code = "";
    deliveryGroupSelectValues.value.name = "";
    deliveryGroupIdSelectValues.value = [];
    shopListItems.value = [];
    areaListItems.value = [];
    deliveryGroupListItems.value = [];
    deliveryGroupItems.value = [];
    tableHeaders.value = [];
    listDataArray.value = [];
    listGroupDataArray.value = [];
    areaParentData.clear();
    mappedListData.clear();
    mappedListGroupData.clear();

    const chainCode = chainSelectValues.value?.chain_code ?? "";
    if (!chainCode) return;

    shopListInput.chain_code = chainCode;
    shopListItems.value = await getShopList(shopListInput);
  };

  /**
   * グループキャップ設定タブ @Click
   */
  const onClickGroupTab = async (): Promise<void> => {
    mode.value = "group";

    // 編集フラグをOFFにする
    processFlag.value = 0;

    if (!shopSelectValues.value) {
      shopSelectValues.value = { shop_code: "", name: "" };
    } else {
      shopSelectValues.value.shop_code = "";
      shopSelectValues.value.name = "";
    }
    areaSelectValues.value = null;

    shopListItems.value = [];
    areaListItems.value = [];
    deliveryGroupListItems.value = [];
    deliveryGroupItems.value = [];

    tableHeaders.value = [];
    listDataArray.value = [];
    listGroupDataArray.value = [];

    areaParentData.clear();
    mappedListData.clear();
    mappedListGroupData.clear();

    if (!chainSelectValues.value?.chain_code) {
      return;
    }

    // 配達グループ情報を取得する
    deliveryGroupListInput.chain_code = chainSelectValues.value.chain_code;
    const groups = await getDeliveryGroupList(deliveryGroupListInput);
    deliveryGroupListItems.value = groups?.map((item) =>
      item
        ? ({
            __typename: "DeliveryGroup",
            chain_code: item.chain_code,
            customer_code: item.customer_code,
            group_code: item.group_code,
            name: item.name,
            result: { __typename: "Result", result: "", message: null },
          })
        : item
    ) ?? [];
  };

  return {
    deliveryGroupListItems,
    deliveryGroupItems,
    onChangeChain,
    onChangeShop,
    onChangeArea,
    onChangeDeliveryGroup,
    onChangeDeliveryGroupId,
    onClickShopTab,
    onClickGroupTab,
  };
};
